/**
 * What the coverage notice says about the ledger's session lookup.
 *
 * Three things can be true of an environment that shares a provider home with
 * another Forge server, and the page has to be plain about which:
 *
 * - Sessions the ledger already holds under another Environment were set
 *   aside, so they are counted once, over there.
 * - The lookup failed, so nothing was set aside and a session may be counted
 *   twice. The totals are not wrong, they are wide.
 * - The environment cannot ask at all, which is every desktop until the
 *   devices-side lookup ships.
 *
 * Counted per environment rather than per transcript directory: a person reads
 * "my MacBook", not "the Claude directory on my MacBook", and the two
 * directories are one machine's worth of sessions.
 *
 * @module usageSessionLookupLines
 */
import type { UsageSourceStatus } from "./useUsage";

export type UsageSessionLookupLineKind = "recordedElsewhere" | "unavailable" | "unsupported";

export interface UsageSessionLookupLine {
  readonly sourceId: string;
  readonly kind: UsageSessionLookupLineKind;
  readonly text: string;
}

interface LookupTotals {
  readonly recordedElsewhere: number;
  readonly unavailable: number;
  readonly unsupported: number;
}

/**
 * Totals one environment's transcript directories.
 *
 * Only transcript sources carry these counts. The Recorded lane reports this
 * environment's own turns and never asks the ledger anything, and CMMD's own
 * source *is* the ledger, so a count on either would be a build sending a
 * field it has no business sending.
 */
function lookupTotals(source: UsageSourceStatus): LookupTotals {
  let recordedElsewhere = 0;
  let unavailable = 0;
  let unsupported = 0;
  for (const reported of source.summary?.sources ?? []) {
    if (reported.fingerprint.kind !== "transcripts") continue;
    recordedElsewhere += reported.recordedElsewhereSessions ?? 0;
    unavailable += reported.lookupUnavailableSessions ?? 0;
    unsupported += reported.lookupUnsupportedSessions ?? 0;
  }
  return { recordedElsewhere, unavailable, unsupported };
}

function sessions(count: number): string {
  return count === 1 ? "1 session" : `${count} sessions`;
}

/**
 * One line per state per environment, in the order a person needs them: what
 * was set aside, then what could not be checked, then what cannot be checked
 * here at all. A count of zero says nothing, so a machine that shares no home
 * with anything shows no line.
 */
export function usageSessionLookupLines(
  sources: readonly UsageSourceStatus[],
): readonly UsageSessionLookupLine[] {
  const lines: UsageSessionLookupLine[] = [];
  for (const source of sources) {
    const totals = lookupTotals(source);
    const { sourceId, label } = source;
    if (totals.recordedElsewhere > 0) {
      lines.push({
        sourceId,
        kind: "recordedElsewhere",
        text: `${sessions(totals.recordedElsewhere)} on ${label} ${
          totals.recordedElsewhere === 1 ? "is" : "are"
        } recorded elsewhere and counted there.`,
      });
    }
    if (totals.unavailable > 0) {
      lines.push({
        sourceId,
        kind: "unavailable",
        text: `${label} could not check ${sessions(totals.unavailable)} against the ledger, so they are still counted here.`,
      });
    }
    if (totals.unsupported > 0) {
      lines.push({
        sourceId,
        kind: "unsupported",
        text: `Checking the ledger is not available on ${label}, so ${sessions(totals.unsupported)} may be counted twice.`,
      });
    }
  }
  return lines;
}
